import moment from "moment";
import {MembershipTypes} from "../Configs/DatabaseConsts";

export const PROMO_CODE_OPTION = {
    UNLIMITED: 'unlimited',
    LIMITED: 'limited',
};

export const PROMO_CODE_MT_TYPE = {
    SERVICE: 'service',
    REGULAR: 'regular',
    RECURRING: 'recurring',
    REGULAR_HUGIM: 'regular-hugim',
    RECURRING_HUGIM: 'recurring-hugim',
    REGULAR_TRACK: 'regular-track',
    RECURRING_TRACK: 'recurring-track',
    WORKSHOP: 'workshop',
    TRIAL: 'trial',
    PLAN: 'plan',
    SESSION: 'session',
};

export const TIERED_PROMO_CODE_MAX = 12;

/**
 * A promo code is only ever offered against recurring membership types when it defines a cycles
 * limit or tiers, and only against non recurring ones otherwise.
 */
export const isPromoCodeRecurring = (promoCode) =>
    (promoCode?.recurring_cycles ?? null) !== null || (promoCode?.tiered_promo_codes?.length ?? 0) > 0;

/**
 * Select options for a "default promo code" input - only codes that can actually be applied on the
 * registration site for the given location and payment type. Internal codes are rejected by
 * promoCode/validate on the site, so they could never auto apply.
 */
export const getDefaultPromoCodeOptions = (promoCodes, locationId, isRecurring) =>
    (promoCodes?.active ?? [])
        .filter(promoCode => !promoCode.internal)
        .filter(promoCode => !promoCode.location_id || promoCode.location_id === locationId)
        .filter(promoCode => isPromoCodeRecurring(promoCode) === isRecurring)
        .map(promoCode => ({...promoCode, value: promoCode.id, label: promoCode.name}));

export const reArrangeMembershipTypes = ({membershipTypes, services, allowHugim, allowPlan, internal}) => {
    const today = moment();
    const res = (membershipTypes ?? []).reduce((group, mt) => {
        if (mt.active !== 1) return group;
        if(internal && ![PROMO_CODE_MT_TYPE.REGULAR, PROMO_CODE_MT_TYPE.RECURRING].includes(mt.type)) return group; //for now we show only hugim mt in internal promo
        if(!allowPlan && mt.type === PROMO_CODE_MT_TYPE.PLAN) return group;
        if(mt.type === MembershipTypes.ONETIME) return group;
        if (mt.series_track_id) {
            if (allowHugim) {
                const trackType = mt.is_recurring_payment ? PROMO_CODE_MT_TYPE.RECURRING_TRACK : PROMO_CODE_MT_TYPE.REGULAR_TRACK;
                group[trackType] = group[trackType] || [];
                group[trackType].push(mt);
            }
            return group;
        }
        if (!mt.is_recurring_payment) {
            const type = mt.type;
            if (type !== PROMO_CODE_MT_TYPE.SERVICE && type !== PROMO_CODE_MT_TYPE.REGULAR) {
                if (type !== PROMO_CODE_MT_TYPE.WORKSHOP || (type === PROMO_CODE_MT_TYPE.WORKSHOP && !allowHugim)) {
                    group[type] = group[type] || [];
                    group[type].push(mt);
                }
            } else if (type === PROMO_CODE_MT_TYPE.REGULAR && mt.series && allowHugim) {
                const hugEndDate = moment(mt.series.end_date, 'YYYY-MM-DD');
                if (hugEndDate.isSameOrAfter(today)) {
                    group[PROMO_CODE_MT_TYPE.REGULAR_HUGIM] = group[PROMO_CODE_MT_TYPE.REGULAR_HUGIM] || [];
                    group[PROMO_CODE_MT_TYPE.REGULAR_HUGIM].push(mt);
                }
            }
        } else {
            if (mt.type !== PROMO_CODE_MT_TYPE.RECURRING) {
                group[PROMO_CODE_MT_TYPE.RECURRING] = group[PROMO_CODE_MT_TYPE.RECURRING] || [];
                group[PROMO_CODE_MT_TYPE.RECURRING].push(mt);
            } else {
                if (mt.series && allowHugim) {
                    const hugEndDate = moment(mt.series.end_date, 'YYYY-MM-DD');
                    if (!mt.series.end_date || hugEndDate.isSameOrAfter(today)) {
                        group[PROMO_CODE_MT_TYPE.RECURRING_HUGIM] = group[PROMO_CODE_MT_TYPE.RECURRING_HUGIM] || [];
                        group[PROMO_CODE_MT_TYPE.RECURRING_HUGIM].push(mt);
                    }
                }
            }
        }
        return group;
    }, {});
    if (services && services.length && !internal) {
        res[PROMO_CODE_MT_TYPE.SERVICE] = services;
    }
    return res;
};

export const filterMembershipsBySelectedLocation = (membershipTypesAllLocations, selectedLocation, locations) => {
    const filtered = {};
    Object.keys(membershipTypesAllLocations).forEach(type => {
        const memberships = membershipTypesAllLocations[type].filter(mt => {
            if (!selectedLocation) return true;
            return mt.location_box_fk === selectedLocation;
        });
        filtered[type] = memberships.map(mt => {
            if (!selectedLocation && locations.length > 1) {
                const mtLocation = locations.find(loc => mt.location_box_fk === loc.id);
                if (type !== PROMO_CODE_MT_TYPE.SERVICE && mtLocation && !mt.name.includes(` (${mtLocation.location})`)) {
                    return { ...mt, name: `${mt.name} (${mtLocation.location})` };
                }
            }
            return mt;
        });
    });
    return filtered;
};

export const getVisibleMembershipTypes = ({membershipTypesWithRecurring, recurringCycles, allowHugim, tieredPromoCodesActive}) => {
    if (recurringCycles !== null) {
        const out = membershipTypesWithRecurring[PROMO_CODE_MT_TYPE.RECURRING] ? { [PROMO_CODE_MT_TYPE.RECURRING]: membershipTypesWithRecurring[PROMO_CODE_MT_TYPE.RECURRING] || [] } : {};
        if (allowHugim && membershipTypesWithRecurring[PROMO_CODE_MT_TYPE.RECURRING_HUGIM] && !tieredPromoCodesActive) {
            out[PROMO_CODE_MT_TYPE.RECURRING_HUGIM] = membershipTypesWithRecurring[PROMO_CODE_MT_TYPE.RECURRING_HUGIM];
        }
        if (allowHugim && membershipTypesWithRecurring[PROMO_CODE_MT_TYPE.RECURRING_TRACK] && !tieredPromoCodesActive) {
            out[PROMO_CODE_MT_TYPE.RECURRING_TRACK] = membershipTypesWithRecurring[PROMO_CODE_MT_TYPE.RECURRING_TRACK];
        }
        return out;
    }
    return Object.keys(membershipTypesWithRecurring).reduce((acc, key) => {
        if (!key.includes('recurring')) {
            acc[key] = membershipTypesWithRecurring[key];
        }
        return acc;
    }, {});
};

export const createSelectedMembershipsInEditMode = ({ target, services, allMembershipTypes, allowHugim }) => {
    const today = moment();
    const memberships = [];
    target.promo_code_membership_types?.forEach(pcmt => {
        memberships.push(...(pcmt.membership_types ?? []));
    });
    return memberships.reduce((group, membershipType) => {
        let type = membershipType.type;
        if (membershipType.series_track_id) {
            type = membershipType.is_recurring_payment ? PROMO_CODE_MT_TYPE.RECURRING_TRACK : PROMO_CODE_MT_TYPE.REGULAR_TRACK;
        } else if (membershipType.is_recurring_payment) {
            type = membershipType.series_id ? PROMO_CODE_MT_TYPE.RECURRING_HUGIM : PROMO_CODE_MT_TYPE.RECURRING;
        } else if (membershipType.series_id && membershipType.type === PROMO_CODE_MT_TYPE.REGULAR) {
            type = PROMO_CODE_MT_TYPE.REGULAR_HUGIM;
        }
        group[type] = group[type] || [];
        if (membershipType.series_id && allowHugim && type !== PROMO_CODE_MT_TYPE.TRIAL) {
            const mt = allMembershipTypes.find(m => m.id === membershipType.id);
            if (mt) {
                const hugEndDate = mt.series?.end_date ? moment(mt.series.end_date, 'YYYY-MM-DD') : null;
                if (hugEndDate && !hugEndDate.isSameOrAfter(today)) {
                    return group;
                }
            }
        }
        if (type === PROMO_CODE_MT_TYPE.SERVICE) {
            const service = (services ?? []).find(s => s.membership_type_ids?.indexOf(membershipType.id) !== -1);
            if (service && !group[type].includes(service.id)) {
                group[type] = group[type].concat(service.id);
            }
        } else {
            group[type].push(membershipType.id);
        }
        return group;
    }, {});
};

export const buildMembershipTypesPayload = (selectedByType, services) => {
    const memberships = [];
    Object.entries(selectedByType ?? {}).forEach(([type, ids]) => {
        (ids ?? []).forEach(id => {
            if (type === PROMO_CODE_MT_TYPE.SERVICE) {
                const service = (services ?? []).find(s => s.id === id);
                if (service?.membership_type_ids) memberships.push(...service.membership_type_ids);
            } else {
                memberships.push(id);
            }
        });
    });
    return memberships;
};

export const checkPromoFlowIsInternal = (initProps = {}, target = null) => {
    if(!!target?.internal) return true
    if(!!initProps?.internal) return true
    return false
}